<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissionStore } from '@/stores/permissions'
import { useUiStore } from '@/stores/ui'
import { useIntegrationStore } from '@/stores/integrations'
import { loadModules } from '@/router/module-loader'
import { isProductivityVisible } from '@/core/consoleCommands'
import { OrganizationSwitcher } from '@/components'

const router = useRouter()
const authStore = useAuthStore()
const permissionStore = usePermissionStore()
const uiStore = useUiStore()
const integrationStore = useIntegrationStore()

const { modules } = loadModules()

// Search state
const searchQuery = ref('')

// Expanded modules state (module ID -> expanded boolean)
const expandedModules = ref<Record<string, boolean>>({})

// Initialize all modules as collapsed by default
modules.forEach(mod => {
  expandedModules.value[mod.id] = false
})

// Toggle module collapse/expand
const toggleModule = (moduleId: string) => {
  expandedModules.value[moduleId] = !expandedModules.value[moduleId]
}

// Check if module is expanded
const isModuleExpanded = (moduleId: string) => {
  return expandedModules.value[moduleId] ?? false
}

// Settings sub-module keys for computing Settings container visibility
const SETTINGS_KEYS = [
  'settings.overview', 'settings.api_clients', 'settings.embed_keys',
  'settings.products', 'settings.users', 'settings.organizations',
  'settings.data_import', 'settings.data_cleanup', 'settings.integrations',
  'settings.workflows', 'settings.workers', 'settings.ai_usage',
]

// Field Operations sub-module keys for computing Field Operations container visibility
const FIELD_OPERATIONS_KEYS = ['toolbox_talks']

// Filter modules based on RBAC permissions, showInSidebar, and integration gate
const roleFilteredModules = computed(() => {
  return modules
    .filter(mod => {
      // Check DevTools console visibility override (rws.productivity.visible = false)
      if (mod.id === 'productivity' && !isProductivityVisible.value) return false

      // Settings container: show if any child settings.* key is visible
      if (mod.id === 'settings') return permissionStore.canViewAny(SETTINGS_KEYS)
      // Field Operations container: show if any child field-operations key is visible
      if (mod.id === 'field-operations') return permissionStore.canViewAny(FIELD_OPERATIONS_KEYS)
      // Super-admin module: only visible to super admins
      if (mod.id === 'super-admin') return authStore.isSuperAdmin

      const moduleKey = mod.navigation.moduleKey
      // Modules without a moduleKey are always visible (e.g., dashboard, saved-views)
      if (!moduleKey) return true
      // Standard module: check permission store
      return permissionStore.canView(moduleKey)
    })
    .filter(mod => mod.navigation.showInSidebar !== false)
    .filter(mod => {
      // If module requires an integration, check if it's enabled
      if (!mod.navigation.requiresIntegration) return true
      if (mod.navigation.requiresIntegration === 'xweather') return integrationStore.isXweatherEnabled
      if (mod.navigation.requiresIntegration === 'apollo') return integrationStore.isApolloEnabled
      return true
    })
    .sort((a, b) => a.navigation.order - b.navigation.order)
})

// Get visible pages for a module (permission-filtered with minAccessLevel support)
const getVisiblePages = (mod: typeof modules[0]) => {
  return mod.navigation.pages.filter(page => {
    const pageKey = page.moduleKey ?? mod.navigation.moduleKey
    // If no module key, page is always visible
    if (!pageKey) return true
    // Use minAccessLevel if specified, otherwise default to view_only
    const requiredLevel = page.minAccessLevel ?? 'view_only'
    return permissionStore.can(pageKey, requiredLevel)
  })
}

// Normalize text for search comparison
const normalizeText = (text: string) => text.toLowerCase().trim()

// Check if a module matches the search query (by label)
const moduleMatchesSearch = (mod: typeof modules[0], query: string) => {
  if (!query) return true
  return normalizeText(mod.navigation.label).includes(normalizeText(query))
}

// Check if a page matches the search query (by label)
const pageMatchesSearch = (page: { label: string }, query: string) => {
  if (!query) return true
  return normalizeText(page.label).includes(normalizeText(query))
}

// Get filtered pages for a module (search + role filtered)
const getFilteredPages = (mod: typeof modules[0]) => {
  const visiblePages = getVisiblePages(mod)
  if (!searchQuery.value) return visiblePages
  return visiblePages.filter(page => pageMatchesSearch(page, searchQuery.value))
}

// Check if a module should be visible after search filtering
const isModuleVisible = (mod: typeof modules[0]) => {
  if (!searchQuery.value) return true
  // Module is visible if its label matches OR any of its pages match
  const matchesModuleLabel = moduleMatchesSearch(mod, searchQuery.value)
  const hasMatchingPages = getFilteredPages(mod).length > 0
  return matchesModuleLabel || hasMatchingPages
}

// Filtered modules (role + search)
const filteredModules = computed(() => {
  return roleFilteredModules.value.filter(mod => isModuleVisible(mod))
})

// Watch search query to auto-expand modules with matching pages
watch(searchQuery, (query) => {
  if (query) {
    // When searching, expand all modules that have matching pages
    roleFilteredModules.value.forEach(mod => {
      if (getFilteredPages(mod).length > 0) {
        expandedModules.value[mod.id] = true
      }
    })
  }
})

// Check if a path is active
const isActive = (path: string) => {
  return router.currentRoute.value.path.startsWith(path)
}

const toggleSidebar = () => {
  uiStore.toggleSidebar()
}

// Clear search
const clearSearch = () => {
  searchQuery.value = ''
}
</script>

<template>
  <aside
    :class="[
      'sidebar',
      { 'sidebar--collapsed': uiStore.sidebarCollapsed }
    ]"
  >
    <!-- Logo Area -->
    <div class="sidebar__header">
      <div class="sidebar__logo">
        <img src="/redis-r.png" alt="RedIS Web Services" class="sidebar__logo-img" />
        <Transition name="fade">
          <span v-if="!uiStore.sidebarCollapsed" class="sidebar__logo-text">
            RWS
          </span>
        </Transition>
      </div>
      
      <button
        class="sidebar__toggle"
        aria-label="Toggle sidebar"
        @click="toggleSidebar"
      >
        <svg
          class="toggle-icon"
          :class="{ 'toggle-icon--rotated': uiStore.sidebarCollapsed }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>
    </div>

    <!-- Search Input (only when not collapsed) -->
    <Transition name="fade">
      <div v-if="!uiStore.sidebarCollapsed" class="sidebar__search">
        <div class="search-input-wrapper">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search modules..."
          />
          <button
            v-if="searchQuery"
            class="search-clear"
            @click="clearSearch"
            aria-label="Clear search"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>

    <!-- Navigation -->
    <nav class="sidebar__nav">
      <!-- No results message -->
      <div v-if="filteredModules.length === 0 && searchQuery" class="no-results">
        <p>No modules match "{{ searchQuery }}"</p>
      </div>

      <template v-for="mod in filteredModules" :key="mod.id">
        <div class="nav-group">
          <!-- Group Header (clickable to expand/collapse) -->
          <div class="nav-group__row">
            <router-link
              :to="mod.navigation.path"
              :class="[
                'nav-group__header',
                { 'nav-group__header--active': isActive(mod.navigation.path) }
              ]"
              :title="uiStore.sidebarCollapsed ? mod.navigation.label : undefined"
            >
              <span class="nav-group__icon">
                <svg v-if="mod.navigation.icon === 'building'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
                  <line x1="9" y1="22" x2="9" y2="2" />
                  <line x1="15" y1="22" x2="15" y2="2" />
                  <line x1="4" y1="8" x2="20" y2="8" />
                  <line x1="4" y1="14" x2="20" y2="14" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'building-2'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                  <path d="M10 6h4" />
                  <path d="M10 10h4" />
                  <path d="M10 14h4" />
                  <path d="M10 18h4" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'users'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'clipboard-list'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <path d="M12 11h4" />
                  <path d="M12 16h4" />
                  <path d="M8 11h.01" />
                  <path d="M8 16h.01" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'scroll-text'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M15 12h-5" />
                  <path d="M15 8h-5" />
                  <path d="M19 17V5a2 2 0 0 0-2-2H4" />
                  <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'settings'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-1.41 3.41 2 2 0 0 1-1.42-.59l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-3.41-1.41 2 2 0 0 1 .59-1.42l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'presentation'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'key'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'package'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'zap'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'home'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'media'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'gauge'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="m12 14 4-4" />
                  <path d="M3.34 19a10 10 0 1 1 17.32 0" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'map'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                  <line x1="8" y1="2" x2="8" y2="18" />
                  <line x1="16" y1="6" x2="16" y2="22" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'cloud-lightning'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M19 16.9A5 5 0 0018 7h-1.26a8 8 0 10-11.62 9" />
                  <polyline points="13 11 9 17 15 17 11 23" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'cloud-rain-wind'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M19 16.9A5 5 0 0018 7h-1.26a8 8 0 10-11.62 9" />
                  <path d="M13 11l-4 6h6l-4 6" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'book-user'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M15 13a3 3 0 1 0-6 0" />
                  <circle cx="12" cy="7" r="2" />
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'qr-code'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="2" width="8" height="8" rx="1" />
                  <rect x="14" y="2" width="8" height="8" rx="1" />
                  <rect x="2" y="14" width="8" height="8" rx="1" />
                  <rect x="14" y="14" width="4" height="4" rx="0.5" />
                  <rect x="20" y="14" width="2" height="2" rx="0.25" />
                  <rect x="14" y="20" width="2" height="2" rx="0.25" />
                  <rect x="20" y="20" width="2" height="2" rx="0.25" />
                  <rect x="5" y="5" width="2" height="2" fill="currentColor" />
                  <rect x="17" y="5" width="2" height="2" fill="currentColor" />
                  <rect x="5" y="17" width="2" height="2" fill="currentColor" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'monitor-smartphone'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="3" width="14" height="11" rx="2" />
                  <path d="M18 8h2a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3" />
                  <path d="M18 16h2" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'bar-chart-3'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'panel-layout'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <rect x="6" y="6" width="5" height="7" rx="0.5" />
                  <rect x="13" y="6" width="5" height="4" rx="0.5" />
                  <rect x="13" y="12" width="5" height="5" rx="0.5" />
                  <rect x="6" y="15" width="5" height="2" rx="0.5" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'scale'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
                  <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
                  <path d="M7 21h10" />
                  <path d="M12 3v18" />
                  <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'ticket'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                  <path d="M13 5v2" />
                  <path d="M13 17v2" />
                  <path d="M13 11v2" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'warehouse'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z" />
                  <path d="M6 18h12" />
                  <path d="M6 14h12" />
                  <rect x="9" y="18" width="6" height="4" rx="1" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'target'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'user-plus'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                <svg v-else-if="mod.navigation.icon === 'hard-hat'" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2z" />
                  <path d="M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5" />
                  <path d="M4 15v-3a6 6 0 0 1 6-6h0" />
                  <path d="M14 6h0a6 6 0 0 1 6 6v3" />
                </svg>
                <svg v-else class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </span>
              <Transition name="fade">
                <span v-if="!uiStore.sidebarCollapsed" class="nav-group__label">
                  {{ mod.navigation.label }}
                </span>
              </Transition>
            </router-link>

            <!-- Expand/Collapse toggle (only if has pages and not collapsed) -->
            <button
              v-if="!uiStore.sidebarCollapsed && getFilteredPages(mod).length > 0"
              class="nav-group__toggle"
              @click.prevent="toggleModule(mod.id)"
              :aria-label="isModuleExpanded(mod.id) ? 'Collapse' : 'Expand'"
            >
              <svg 
                class="chevron-icon"
                :class="{ 'chevron-icon--expanded': isModuleExpanded(mod.id) }"
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>

          <!-- Sub-pages (collapsible) -->
          <Transition name="slide">
            <div 
              v-if="!uiStore.sidebarCollapsed && isModuleExpanded(mod.id) && getFilteredPages(mod).length > 0" 
              class="nav-group__items"
            >
              <template
                v-for="page in getFilteredPages(mod)"
                :key="page.path"
              >
                <!-- Disabled page (integration not enabled) -->
                <span
                  v-if="page.requiresIntegration && !integrationStore[page.requiresIntegration === 'xweather' ? 'isXweatherEnabled' : 'isApolloEnabled']"
                  :class="[
                    'nav-item',
                    'nav-item--disabled'
                  ]"
                  :title="`Requires ${page.requiresIntegration} integration`"
                >
                  <svg class="nav-item__lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  {{ page.label }}
                </span>
                <!-- Active page -->
                <router-link
                  v-else
                  :to="page.path"
                  :class="[
                    'nav-item',
                    { 'nav-item--active': router.currentRoute.value.path === page.path }
                  ]"
                >
                  {{ page.label }}
                </router-link>
              </template>
            </div>
          </Transition>
        </div>
      </template>
    </nav>

    <!-- User Section -->
    <div class="sidebar__footer">
      <!-- Organization Switcher -->
      <OrganizationSwitcher :collapsed="uiStore.sidebarCollapsed" />
      
      <router-link 
        to="/profile"
        :class="[
          'user-card',
          { 'user-card--collapsed': uiStore.sidebarCollapsed }
        ]"
      >
        <div class="user-card__avatar">
          {{ authStore.displayName?.charAt(0)?.toUpperCase() || '?' }}
        </div>
        <Transition name="fade">
          <div v-if="!uiStore.sidebarCollapsed" class="user-card__info">
            <div class="user-card__name">{{ authStore.displayName || 'User' }}</div>
            <div v-if="authStore.isSuperAdmin" class="user-card__super-admin">Super Admin</div>
          </div>
        </Transition>
      </router-link>

      <!-- Privacy / Legal -->
      <router-link
        to="/privacy"
        :class="[
          'sidebar__legal',
          { 'sidebar__legal--collapsed': uiStore.sidebarCollapsed }
        ]"
        :title="uiStore.sidebarCollapsed ? 'Privacy Policy' : undefined"
      >
        <svg class="legal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <Transition name="fade">
          <span v-if="!uiStore.sidebarCollapsed" class="legal-label">Privacy Policy</span>
        </Transition>
      </router-link>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-color);
  transition: width var(--transition-slow), background-color var(--transition-theme), border-color var(--transition-theme);
  width: var(--sidebar-width);
  z-index: var(--z-fixed);
}

.sidebar--collapsed {
  width: var(--sidebar-collapsed-width);
}

.sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  height: var(--header-height);
  border-bottom: 1px solid var(--border-color);
}

.sidebar__logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sidebar__logo-img {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  object-fit: contain;
  image-rendering: -webkit-optimize-contrast;
}

.sidebar__logo-text {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  font-family: var(--font-display);
}

.sidebar__toggle {
  padding: 0.4rem;
  border-radius: var(--radius-md);
  color: var(--text-muted);
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.sidebar__toggle:hover {
  color: var(--text-secondary);
  background: var(--bg-hover);
}

.toggle-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease;
}

.toggle-icon--rotated {
  transform: rotate(180deg);
}

.sidebar__nav {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
}

/* Search Input */
.sidebar__search {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 0.625rem;
  width: 16px;
  height: 16px;
  color: var(--text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 2rem;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--text-sm);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input::placeholder {
  color: var(--text-disabled);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.1);
}

.search-clear {
  position: absolute;
  right: 0.375rem;
  width: 20px;
  height: 20px;
  padding: 0.125rem;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color 0.2s, background 0.2s;
}

.search-clear:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.search-clear svg {
  width: 100%;
  height: 100%;
}

/* No results message */
.no-results {
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.nav-group {
  margin-bottom: 0.25rem;
}

/* Flex row for header + toggle */
.nav-group__row {
  display: flex;
  align-items: center;
}

.nav-group__header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.2s;
}

.nav-group__header:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.nav-group__header--active {
  color: var(--color-primary);
  background: var(--color-info-bg);
}

.nav-group__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-group__icon .icon {
  width: 20px;
  height: 20px;
}

.nav-group__label {
  font-size: var(--text-sm);
  font-weight: 500;
  white-space: nowrap;
  flex-grow: 1;
}

/* Expand/Collapse toggle button */
.nav-group__toggle {
  width: 24px;
  height: 24px;
  padding: 0.25rem;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color 0.2s, background 0.2s;
  flex-shrink: 0;
}

.nav-group__toggle:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.chevron-icon {
  width: 100%;
  height: 100%;
  transition: transform 0.2s ease;
  transform: rotate(-90deg);
}

.chevron-icon--expanded {
  transform: rotate(0deg);
}

.nav-group__items {
  margin-left: 2rem;
  margin-top: 0.25rem;
}

.nav-item {
  display: block;
  padding: 0.375rem 0.625rem;
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-decoration: none;
  transition: all 0.2s;
}

.nav-item:hover {
  color: var(--text-secondary);
  background: var(--bg-hover);
}

.nav-item--active {
  color: var(--color-primary);
  background: var(--color-info-bg);
}

.nav-item--disabled {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.nav-item__lock {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.sidebar__footer {
  padding: 0.625rem;
  border-top: 1px solid var(--border-color);
}

.user-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.user-card:hover {
  background: var(--bg-hover);
}

.user-card--collapsed {
  justify-content: center;
  padding: 0.5rem;
}

.user-card__avatar {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
}

.user-card__info {
  min-width: 0;
}

.user-card__name {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-card__role {
  font-size: 0.7rem;
  color: #6b7280;
  text-transform: capitalize;
}

.user-card__super-admin {
  font-size: 0.65rem;
  font-weight: 600;
  color: #ef4444;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Organization Badge */
.organization-badge {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  background: var(--color-info-bg);
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-md);
}

.organization-badge--collapsed {
  justify-content: center;
  padding: 0.5rem;
}

.organization-badge__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--color-primary);
}

.organization-badge__icon svg {
  width: 100%;
  height: 100%;
}

.organization-badge__info {
  min-width: 0;
  flex: 1;
}

.organization-badge__label {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.organization-badge__name {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Privacy / Legal link */
.sidebar__legal {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  margin-top: 0.5rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  transition: all 0.2s;
}

.sidebar__legal--collapsed {
  justify-content: center;
  padding: 0.375rem;
}

.legal-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--text-disabled);
  transition: color 0.2s;
}

.legal-label {
  font-size: 0.6875rem;
  color: var(--text-disabled);
  transition: color 0.2s;
}

.sidebar__legal:hover .legal-icon,
.sidebar__legal:hover .legal-label {
  color: var(--text-secondary);
}
</style>
